import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { Card } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
import logo from '../assets/help-avatar.svg';
import HelpBoxCompanionModel from '../models/HelpBoxCompanionModel';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { SmallText } from './ui/Typography';

interface HelpBoxCompanionProps {
  children?: React.ReactNode;
}

const AVATAR_SIZE = 116;
const BOX_WIDTH = 420;

const HelpBoxCompanion: React.FC<HelpBoxCompanionProps> = observer(() => {
  const { t } = useTranslation();
  const [showExpanded, setShowExpanded] = React.useState(true);
  const [delayedShow, setDelayedShow] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Delay initial appearance
  React.useEffect(() => {
    const timer = setTimeout(() => setDelayedShow(true), 500);
    return () => clearTimeout(timer);
  }, []);

  // Animation variants
  const boxVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
    exit: { opacity: 0, y: 40, transition: { duration: 0.3 } },
  };

  const avatarVariants = {
    expanded: { width: AVATAR_SIZE, height: AVATAR_SIZE, bottom: -45, right: -70 },
    collapsed: { width: 70, height: 70, bottom: -36, right: -60 }
  };

  // Collapsed avatar click handler
  const handleAvatarClick = () => {
    setShowExpanded(true);
  };

  // Close button handler
  const handleClose = () => {
    setShowExpanded(false);
  };

  // If displayHelpBox is false, do not render anything
  if (!HelpBoxCompanionModel.displayHelpBox) {
    return '';
  }

  return (
    <>
      {/* Expanded Help Box */}
      <AnimatePresence>
        {delayedShow && showExpanded && (
          <motion.div
            key={HelpBoxCompanionModel.helpBoxTitleMessage}
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={boxVariants}
            style={{
              position: 'fixed',
              bottom: isMobile ? 50 : 145,
              right: isMobile ? 50 : 120,
              zIndex: 1000,
              width: BOX_WIDTH,
              maxWidth: '90vw',
            }}
          >
            <Card
              style={{
                borderRadius: 16,
                boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                position: 'relative'
              }}
              styles={{
                body: {
                  padding: 24,
                  paddingRight: 48,
                  paddingBottom: 18
                }
              }}>
              {/* close button */}
              <button
                onClick={handleClose}
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  background: 'transparent',
                  border: 'none',
                  fontSize: 20,
                  cursor: 'pointer',
                  zIndex: 2,
                }}
                aria-label={t('Close help box')}>
                <CloseCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }}/>
              </button>

              {/* title and paragraph text */}
              <SmallText style={{ fontWeight: 'bold' }} >{t(HelpBoxCompanionModel.helpBoxTitleMessage)}</SmallText>
              <SmallText style={{ lineHeight: 1.8 }} >{t(HelpBoxCompanionModel.helpBoxParagraphMessage)}</SmallText>
              {/* //image with click handle  */}
              <motion.img
                src={logo}
                alt="Help"
                initial="expanded"
                animate="expanded"
                variants={avatarVariants}
                style={{
                  position: 'absolute',
                  right: 16,
                  bottom: 8,
                  width: AVATAR_SIZE,
                  height: AVATAR_SIZE,
                  borderRadius: 12,
                  objectFit: 'cover',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onClick={handleClose}
              />
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Collapsed Avatar */}
      <AnimatePresence>
        {delayedShow && !showExpanded && (
          <motion.div
            key="avatar"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'fixed',
              bottom: isMobile ? 10 : 100,
              right: isMobile ? 10 : 36, 
              zIndex: 1000,
              width: AVATAR_SIZE,
              height: AVATAR_SIZE,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
            onClick={handleAvatarClick}
            aria-label={t('Open help box')}
            tabIndex={0}
          >
            <motion.img
              src={logo}
              alt="Help"
              initial="collapsed"
              animate="collapsed"
              variants={avatarVariants}
              style={{
                width: AVATAR_SIZE,
                height: AVATAR_SIZE,
                borderRadius: '50%',
                objectFit: 'cover',
                transition: 'all 0.2s'
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
});

export default HelpBoxCompanion